<script setup>
// Assistente de IA — a tela onde o gestor ajusta o comportamento do assistente.
//
// Cada campo tem um texto de ajuda que diz o EFEITO de mexer nele. Um número sem explicação
// convida a mexer no escuro, e aqui mexer no escuro custa dinheiro e conversa com cliente.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const { accountScopedRoute } = useAccount();
const { carregando, erro, chamar } = useAssistenteIaApi();

// Reativo: trocar de assistente pela URL (ou pelo menu) tem de recarregar a tela, senao ela
// continua mostrando o assistente anterior (AIA-07, C8).
const assistenteId = computed(() => route.params.assistenteId);
const assistente = ref(null);
const credenciais = ref([]);
const modelos = ref([]);
const salvando = ref(false);
const salvo = ref(false);
const frasesTexto = ref('');

const PADROES_BUSCA = { quantidade_trechos: 6, similaridade_minima: 0.3 };
const PADROES_ATENDIMENTO = {
  espera_agrupamento_segundos: 3,
  mensagens_de_contexto: 12,
  pausar_ao_atribuir_agente: true,
  pausar_ao_atribuir_time: false,
  retomar_apos_resolvida: true,
  falhas_para_passar_humano: 2,
};

const credenciaisDeTexto = computed(() =>
  credenciais.value.filter(c => c.finalidade === 'texto' && c.ativa)
);
const credenciaisDeVetorizacao = computed(() =>
  credenciais.value.filter(c => c.finalidade === 'vetorizacao' && c.ativa)
);
const modelosDeTexto = computed(() =>
  modelos.value.filter(m => m.finalidade === 'texto')
);

// De QUEM é o que está na tela. Sem isto, uma troca rápida A → B com a resposta de A chegando
// depois deixava a tela com os dados de A e a URL de B — e "Salvar" gravava a configuração de A
// no id de B (AIA-09, C2).
const carregadoDe = ref(null);

// O erro da chamada PRINCIPAL manda na tela.
//
// Antes eram três `chamar` seguidos e cada um limpava o erro do anterior: assistente apagado em
// outra aba → o primeiro dava 404 e escrevia o erro → credenciais e modelos respondiam 200 e
// apagavam o erro → a tela ficava no spinner para sempre, sem erro e sem o link de volta
// (AIA-08, C1). Agora, se a principal falha, as outras nem saem; e quando saem, elas não têm
// permissão para limpar o erro de ninguém.
const carregar = async () => {
  const pedidoDe = assistenteId.value;
  if (!pedidoDe) return;

  const dados = await chamar(assistenteIaApi.assistente(pedidoDe));
  // a pessoa trocou de assistente enquanto a resposta vinha: esta resposta é de outra tela
  if (pedidoDe !== assistenteId.value) return;
  if (!dados) return;

  assistente.value = dados.payload;
  carregadoDe.value = pedidoDe;
  frasesTexto.value = (dados.payload.atendimento.frases_pedido_humano || []).join('\n');

  const lista = await chamar(assistenteIaApi.credenciais(), { limparErro: false });
  if (pedidoDe !== assistenteId.value) return;
  if (lista) credenciais.value = lista.payload;

  const disponiveis = await chamar(assistenteIaApi.modelos(), { limparErro: false });
  if (pedidoDe !== assistenteId.value) return;
  if (disponiveis) modelos.value = disponiveis.payload;
};

const salvar = async () => {
  // O que está na tela TEM de ser do assistente da URL. Se não for, a tela está no meio de uma
  // troca e salvar agora gravaria a configuração de um no id do outro (AIA-09, C2).
  if (!assistente.value || carregadoDe.value !== assistenteId.value) {
    // O aviso vem DEPOIS do `carregar()`: o `chamar` zera o erro ao começar, então escrevê-lo
    // antes fazia o banner aparecer e sumir no mesmo instante — a pessoa não via nada
    // (AIA-10, C4).
    await carregar();
    erro.value = t('ASSISTENTE_IA.EDITAR.RECARREGANDO');
    return;
  }

  salvando.value = true;
  salvo.value = false;

  const corpo = {
    nome: assistente.value.nome,
    descricao: assistente.value.descricao,
    modelo: assistente.value.modelo,
    temperatura: assistente.value.temperatura,
    max_tokens_resposta: assistente.value.max_tokens_resposta,
    instrucoes: assistente.value.instrucoes,
    sobre_a_empresa: assistente.value.sobre_a_empresa,
    mensagem_passagem_humano: assistente.value.mensagem_passagem_humano,
    mensagem_passagem_fora_do_horario:
      assistente.value.mensagem_passagem_fora_do_horario,
    credencial_texto_id: assistente.value.credencial_texto_id,
    credencial_vetorizacao_id: assistente.value.credencial_vetorizacao_id,
    busca: assistente.value.busca,
    atendimento: {
      ...assistente.value.atendimento,
      frases_pedido_humano: frasesTexto.value
        .split('\n')
        .map(frase => frase.trim())
        .filter(Boolean),
    },
  };

  // O id vai do que foi CARREGADO, não do que a URL diz agora: os dois são o mesmo por causa
  // da guarda acima, e amarrar no carregado deixa isso explícito para quem ler.
  const salvandoDe = carregadoDe.value;
  const dados = await chamar(assistenteIaApi.editarAssistente(salvandoDe, corpo));

  // A guarda de cima olha ANTES de enviar. Esta olha DEPOIS: "Salvar" em A, troca para B, o GET
  // de B volta primeiro, e a resposta do PATCH de A chegava e punha A na tela de B — e o
  // "Salvar" seguinte, já passando pela guarda, gravava A no id de B (AIA-10, C1).
  if (salvandoDe !== assistenteId.value) return;

  salvando.value = false;
  if (!dados) return;

  assistente.value = dados.payload;
  carregadoDe.value = salvandoDe;
  salvo.value = true;
};

const alternarAtivacao = async () => {
  // Mesma regra do `salvar`: o id vem do que foi CARREGADO, e a resposta só entra na tela se
  // ela ainda for a tela daquele assistente (AIA-10, C1).
  if (!assistente.value || carregadoDe.value !== assistenteId.value) {
    // O aviso vem DEPOIS do `carregar()`: o `chamar` zera o erro ao começar, então escrevê-lo
    // antes fazia o banner aparecer e sumir no mesmo instante — a pessoa não via nada
    // (AIA-10, C4).
    await carregar();
    erro.value = t('ASSISTENTE_IA.EDITAR.RECARREGANDO');
    return;
  }

  const alvo = carregadoDe.value;
  const dados = assistente.value.ativo
    ? await chamar(assistenteIaApi.desativarAssistente(alvo))
    : await chamar(assistenteIaApi.ativarAssistente(alvo));

  if (alvo !== assistenteId.value) return;
  if (dados) assistente.value = dados.payload;
};

const restaurarBusca = () => {
  assistente.value.busca = { ...PADROES_BUSCA };
};

const restaurarAtendimento = () => {
  assistente.value.atendimento = {
    ...assistente.value.atendimento,
    ...PADROES_ATENDIMENTO,
  };
};

onMounted(carregar);
// Ao SAIR da rota, o vue-router zera os params antes de a tela ser destruída, e o watch
// disparava com o id vazio: saía um GET /assistentes/undefined a cada troca de tela
// (AIA-08, C4).
watch(assistenteId, id => {
  if (!id) return;

  assistente.value = null;
  carregadoDe.value = null;
  carregar();
});
</script>

<template>
  <section v-if="assistente" class="flex flex-col gap-6">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>
    <Banner v-if="salvo" color="teal">{{ t('ASSISTENTE_IA.COMUM.SALVO') }}</Banner>

    <div class="flex items-center justify-between">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.TITULO') }} — {{ assistente.nome }}
      </h2>
      <div class="flex gap-2">
        <Button
          :label="
            assistente.ativo
              ? t('ASSISTENTE_IA.EDITAR.DESATIVAR')
              : t('ASSISTENTE_IA.EDITAR.ATIVAR')
          "
          :color="assistente.ativo ? 'ruby' : 'teal'"
          size="sm"
          @click="alternarAtivacao"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.SALVAR')"
          :is-loading="salvando"
          size="sm"
          @click="salvar"
        />
      </div>
    </div>

    <!-- Geral -->
    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.GERAL') }}
      </h3>

      <Input v-model="assistente.nome" :label="t('ASSISTENTE_IA.ASSISTENTES.NOME')" />
      <Input
        v-model="assistente.descricao"
        :label="t('ASSISTENTE_IA.ASSISTENTES.DESCRICAO')"
      />

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.CREDENCIAL_TEXTO') }}
        <select
          v-model="assistente.credencial_texto_id"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        >
          <option :value="null">{{ t('ASSISTENTE_IA.EDITAR.SEM_CREDENCIAL') }}</option>
          <option v-for="c in credenciaisDeTexto" :key="c.id" :value="c.id">
            {{ c.rotulo }} ({{ c.provedor }})
          </option>
        </select>
        <span class="text-n-slate-11">
          {{ t('ASSISTENTE_IA.EDITAR.CREDENCIAL_TEXTO_AJUDA') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.MODELO') }}
        <input
          v-model="assistente.modelo"
          list="modelos-do-assistente"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
        <datalist id="modelos-do-assistente">
          <option v-for="m in modelosDeTexto" :key="m.id" :value="m.id">
            {{ m.rotulo }}
          </option>
        </datalist>
        <span class="text-n-slate-11">{{ t('ASSISTENTE_IA.EDITAR.MODELO_AJUDA') }}</span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.CREDENCIAL_VETORIZACAO') }}
        <select
          v-model="assistente.credencial_vetorizacao_id"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        >
          <option :value="null">{{ t('ASSISTENTE_IA.EDITAR.SEM_CREDENCIAL') }}</option>
          <option v-for="c in credenciaisDeVetorizacao" :key="c.id" :value="c.id">
            {{ c.rotulo }}
          </option>
        </select>
        <span class="text-n-slate-11">
          {{ t('ASSISTENTE_IA.EDITAR.CREDENCIAL_VETORIZACAO_AJUDA') }}
        </span>
      </label>

      <div class="grid gap-3 sm:grid-cols-2">
        <Input
          v-model="assistente.temperatura"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.TEMPERATURA')"
          :message="t('ASSISTENTE_IA.EDITAR.TEMPERATURA_AJUDA')"
        />
        <Input
          v-model="assistente.max_tokens_resposta"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.MAX_TOKENS')"
          :message="t('ASSISTENTE_IA.EDITAR.MAX_TOKENS_AJUDA')"
        />
      </div>
    </div>

    <!-- Instruções -->
    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.INSTRUCOES') }}
      </h3>

      <!-- "Sobre a empresa" vem ANTES das instruções de propósito: é o que a empresa É, e sem
           isso o assistente não sabe o que perguntar nem o que é assunto de fora. As regras de
           comportamento vêm depois (AIA-11, U4). -->
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.SOBRE_A_EMPRESA') }}
        <textarea
          v-model="assistente.sobre_a_empresa"
          rows="4"
          maxlength="2000"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
        <span class="text-n-slate-11">
          {{ t('ASSISTENTE_IA.EDITAR.SOBRE_A_EMPRESA_AJUDA') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.INSTRUCOES_CAMPO') }}
        <textarea
          v-model="assistente.instrucoes"
          rows="8"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
        <span class="text-n-slate-11">
          {{ t('ASSISTENTE_IA.EDITAR.INSTRUCOES_AJUDA') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.MENSAGEM_PASSAGEM') }}
        <textarea
          v-model="assistente.mensagem_passagem_humano"
          rows="2"
          maxlength="1000"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
        <span class="text-n-slate-11">
          {{ t('ASSISTENTE_IA.EDITAR.MENSAGEM_PASSAGEM_AJUDA') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.MENSAGEM_FORA_HORARIO') }}
        <textarea
          v-model="assistente.mensagem_passagem_fora_do_horario"
          rows="2"
          maxlength="1000"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
        <span class="text-n-slate-11">
          {{ t('ASSISTENTE_IA.EDITAR.MENSAGEM_FORA_HORARIO_AJUDA') }}
        </span>
      </label>
    </div>

    <!-- Busca -->
    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTENTE_IA.EDITAR.BUSCA') }}
        </h3>
        <Button
          :label="t('ASSISTENTE_IA.EDITAR.RESTAURAR')"
          variant="faded"
          color="slate"
          size="xs"
          @click="restaurarBusca"
        />
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <Input
          v-model="assistente.busca.quantidade_trechos"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.QUANTIDADE_TRECHOS')"
          :message="t('ASSISTENTE_IA.EDITAR.QUANTIDADE_TRECHOS_AJUDA')"
        />
        <Input
          v-model="assistente.busca.similaridade_minima"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.SIMILARIDADE')"
          :message="t('ASSISTENTE_IA.EDITAR.SIMILARIDADE_AJUDA')"
        />
      </div>
    </div>

    <!-- Atendimento -->
    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTENTE_IA.EDITAR.ATENDIMENTO') }}
        </h3>
        <Button
          :label="t('ASSISTENTE_IA.EDITAR.RESTAURAR')"
          variant="faded"
          color="slate"
          size="xs"
          @click="restaurarAtendimento"
        />
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <Input
          v-model="assistente.atendimento.espera_agrupamento_segundos"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.ESPERA')"
          :message="t('ASSISTENTE_IA.EDITAR.ESPERA_AJUDA')"
        />
        <Input
          v-model="assistente.atendimento.mensagens_de_contexto"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.CONTEXTO')"
          :message="t('ASSISTENTE_IA.EDITAR.CONTEXTO_AJUDA')"
        />
        <Input
          v-model="assistente.atendimento.falhas_para_passar_humano"
          type="number"
          :label="t('ASSISTENTE_IA.EDITAR.FALHAS')"
        />
      </div>

      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="assistente.atendimento.pausar_ao_atribuir_agente" type="checkbox" />
        {{ t('ASSISTENTE_IA.EDITAR.PAUSAR_AGENTE') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="assistente.atendimento.pausar_ao_atribuir_time" type="checkbox" />
        {{ t('ASSISTENTE_IA.EDITAR.PAUSAR_TIME') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="assistente.atendimento.retomar_apos_resolvida" type="checkbox" />
        {{ t('ASSISTENTE_IA.EDITAR.RETOMAR_RESOLVIDA') }}
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.EDITAR.FRASES') }}
        <textarea
          v-model="frasesTexto"
          rows="6"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
        <span class="text-n-slate-11">{{ t('ASSISTENTE_IA.EDITAR.FRASES_AJUDA') }}</span>
      </label>
    </div>
  </section>

  <!-- Erro de carregamento fica FORA do v-if do assistente: com 404 (apagado em outra aba) ou
       401, a tela girava para sempre e ninguem entendia por que (AIA-07, C1). -->
  <div v-else-if="erro" class="flex flex-col gap-3">
    <Banner color="ruby">{{ erro }}</Banner>
    <router-link
      :to="accountScopedRoute('assistente_ia_home')"
      class="text-n-blue-11 hover:underline w-fit"
    >
      {{ t('ASSISTENTE_IA.COMUM.VOLTAR') }}
    </router-link>
  </div>

  <div v-else class="flex justify-center py-16">
    <Spinner />
  </div>
</template>
